/* TREEZYN cart rewards. Shopify's automatic discount remains the price authority. */
(() => {
  if (window.TreezynCartRewards) return;
  const marker = 'late-seezyn-400-v1';
  const root = window.Shopify?.routes?.root || '/';
  let config;
  let failure = '';
  let failedSignature = '';
  let unsafe = false;
  let dirty = false;
  const locked = new Map();

  function lockCart(busy) {
    if (busy) {
      document.querySelectorAll('cart-drawer-items, cart-items, .cart__dynamic-checkout-buttons').forEach((node) => {
        if (!locked.has(node)) locked.set(node, node.inert);
        node.inert = true;
      });
    } else {
      locked.forEach((wasInert, node) => { node.inert = wasInert; });
      locked.clear();
    }
  }

  const marked = (item) => item.properties?._treezyn_reward === marker;
  const gifts = (cart) => (cart.items || []).filter(marked);
  // Subtract promotional lines from the discounted total, never their compare-at prices.
  // Purchased, unmarked beanies/gaiters stay ordinary merchandise and are never removed.
  const spend = (cart) => Math.max(0, Number(cart.total_price || 0) - gifts(cart).reduce((sum, item) => sum + Number(item.final_line_price || 0), 0));
  const supported = (cart) => config?.enabled === true && cart.currency === config.currency && config.currency === 'USD';
  const signature = (cart) => JSON.stringify([cart.currency, spend(cart), (cart.items || []).filter((item) => !marked(item)).map((item) => [item.key, item.quantity, item.final_line_price])]);
  const complete = (cart) => config.gifts.length === 2 && config.gifts.every((gift) => {
    const lines = gifts(cart).filter((item) => String(item.variant_id) === String(gift.id));
    return lines.reduce((sum, item) => sum + item.quantity, 0) === 1 && lines.every((item) => item.final_line_price === 0);
  });
  const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);
  function element(tag, className, text) {
    const node = document.createElement(tag);
    node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  async function request(path, data) {
    const options = { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } };
    if (data) {
      options.method = 'POST';
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(data);
      dirty = true; // A request can succeed on Shopify even if its response is interrupted.
      lockCart(true);
    }
    const response = await fetch(`${root}cart${path}.js`, options);
    const result = await response.json();
    if (!response.ok || result.status) throw new Error('Reward cart request failed');
    return result;
  }

  async function removeGifts(cart) {
    const updates = Object.fromEntries(gifts(cart).map((item) => [item.key, 0]));
    return Object.keys(updates).length ? request('/update', { updates }) : cart;
  }

  async function refreshCart(cart) {
    const drawer = document.querySelector('cart-drawer');
    const targets = [];
    if (drawer) targets.push({ id: 'cart-drawer', selector: '#CartDrawer' });
    if (document.getElementById('cart-icon-bubble')) targets.push({ id: 'cart-icon-bubble', selector: '#cart-icon-bubble', source: '.shopify-section' });
    for (const id of ['main-cart-items', 'main-cart-footer']) {
      const node = document.getElementById(id);
      if (node) targets.push({ id: node.dataset.id, selector: `#${id} .js-contents`, source: '.js-contents' });
    }
    if (!targets.length) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.set('sections', targets.map((target) => target.id).join(','));
    const response = await fetch(url.href, { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error('Reward display refresh failed');
    const sections = await response.json();
    const replacements = targets.map((target) => {
      const html = sections[target.id];
      const source = html && new DOMParser().parseFromString(html, 'text/html').querySelector(target.source || target.selector);
      if (!source) throw new Error('Reward cart section unavailable');
      return { target: document.querySelector(target.selector), html: source.innerHTML };
    });
    const focusedInDrawer = drawer?.contains(document.activeElement);
    replacements.forEach(({ target, html }) => { if (target) target.innerHTML = html; });
    document.querySelectorAll('#cart-live-region-text, #CartDrawer-LiveRegionText').forEach((node) => {
      node.textContent = `Cart updated. ${cart.item_count} items. Estimated total ${money(cart.total_price)}.`;
    });
    drawer?.classList.toggle('is-empty', !cart.item_count);
    document.querySelector('cart-items')?.classList.toggle('is-empty', !cart.item_count);
    document.getElementById('main-cart-footer')?.classList.toggle('is-empty', !cart.item_count);
    drawer?.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
    if (drawer?.classList.contains('active') && focusedInDrawer && typeof trapFocus === 'function') {
      trapFocus(drawer.querySelector('.drawer__inner'), drawer.querySelector('.drawer__close'));
    }
  }

  function render(cart) {
    document.querySelectorAll('.tz-cart-rewards').forEach((node) => node.remove());
    if ((!supported(cart) || !cart.item_count) && !failure) return;
    const header = document.querySelector('cart-drawer .drawer__header');
    const pageHeading = document.querySelector('cart-items .title-wrapper-with-link');
    const amount = spend(cart);
    const shipping = amount >= config.shippingThreshold;
    const earned = amount >= config.giftThreshold;
    let message = !shipping ? `You're ${money(config.shippingThreshold - amount)} away from free shipping.`
      : !earned ? `Free shipping unlocked. Add ${money(config.giftThreshold - amount)} for your free gift set.`
      : complete(cart) ? 'Both rewards unlocked. Your free beanie + gaiter are in the cart.'
      : 'Free shipping unlocked. Your cart qualifies for the free gift set.';
    if (failure) message = failure;
    for (const target of [header, pageHeading].filter(Boolean)) {
      const box = element('section', 'tz-cart-rewards');
      box.setAttribute('aria-label', 'Cart rewards');
      const status = element('p', 'tz-cart-rewards__message', message);
      status.setAttribute('role', 'status');
      const track = element('div', 'tz-cart-rewards__track');
      track.setAttribute('role', 'progressbar');
      track.setAttribute('aria-label', 'Progress toward cart rewards');
      track.setAttribute('aria-valuemin', '0');
      track.setAttribute('aria-valuemax', String(config.giftThreshold / 100));
      track.setAttribute('aria-valuenow', String(Math.min(amount, config.giftThreshold) / 100));
      track.setAttribute('aria-valuetext', message);
      const fill = element('span', 'tz-cart-rewards__fill');
      fill.style.width = `${Math.min(100, amount / config.giftThreshold * 100)}%`;
      const milestone = element('span', `tz-cart-rewards__milestone${shipping ? ' is-earned' : ''}`);
      milestone.style.left = `${config.shippingThreshold / config.giftThreshold * 100}%`;
      track.append(fill, milestone);
      const tiers = element('div', 'tz-cart-rewards__tiers');
      tiers.append(element('span', shipping ? 'is-earned' : '', `${shipping ? '✓ ' : ''}${money(config.shippingThreshold)} · Free shipping`), element('span', earned && complete(cart) ? 'is-earned' : '', `${earned && complete(cart) ? '✓ ' : ''}${money(config.giftThreshold)} · Free beanie + gaiter`));
      box.append(status);
      if (supported(cart) && cart.item_count) box.append(track, tiers);
      if (failure) {
        const retry = element('button', 'tz-cart-rewards__retry', 'Retry gift offer');
        retry.type = 'button';
        retry.addEventListener('click', () => {
          failedSignature = '';
          retry.disabled = true;
          document.dispatchEvent(new CustomEvent('treezyn:cart:rewards-retry'));
        });
        box.append(retry);
      }
      box.append(element('p', 'tz-cart-rewards__terms', 'Late Seezyn gift set · One per order · Qualifying subtotal after discounts, before tax and shipping. Gifts excluded. Shipping eligibility confirmed at checkout.'));
      target.insertAdjacentElement('afterend', box);
    }
  }

  async function sync(cart, settings) {
    config = settings;
    if (!config) return cart;
    unsafe = false;
    const eligible = supported(cart) && spend(cart) >= config.giftThreshold;
    const sig = signature(cart);
    const beforeSpend = spend(cart);
    let discountConflict = false;
    if (sig !== failedSignature) failure = '';
    try {
      if (!eligible) {
        cart = await removeGifts(cart);
        failedSignature = '';
        failure = '';
      } else if (sig !== failedSignature) {
        if (config.gifts.length !== 2 || config.gifts.some((gift) => !gift.id || !gift.available)) throw new Error('Gift unavailable');
        // Normalize only tagged lines; keys can change after discounts, so always use fresh cart data.
        const updates = {};
        const seen = new Set();
        for (const item of gifts(cart)) {
          const id = String(item.variant_id);
          const quantity = config.gifts.some((gift) => String(gift.id) === id) && !seen.has(id) ? 1 : 0;
          seen.add(id);
          if (item.quantity !== quantity) updates[item.key] = quantity;
        }
        if (Object.keys(updates).length) cart = await request('/update', { updates });
        const missing = config.gifts.filter((gift) => !gifts(cart).some((item) => String(item.variant_id) === String(gift.id)));
        if (missing.length) {
          await request('/add', { items: missing.map((gift) => ({ id: gift.id, quantity: 1, properties: { _treezyn_reward: marker } })) });
          cart = await request('');
          // A free gift must not replace a better/other discount and raise the paid total.
          discountConflict = spend(cart) > beforeSpend;
          if (discountConflict) throw new Error('Gift replaces existing savings');
        }
        // Never leave automatically added merchandise at a paid price, or let it fund itself.
        if (spend(cart) < config.giftThreshold || !complete(cart)) throw new Error('Gift discount not applied');
      } else if (gifts(cart).length && !complete(cart)) {
        cart = await removeGifts(cart);
      }
    } catch (error) {
      failure = discountConflict
        ? 'The free gift set can’t combine with your current discount. Your existing savings have been kept.'
        : 'We couldn’t apply your free gift set. Please retry or continue with your selected gear.';
      try {
        cart = await request('');
        cart = await removeGifts(cart);
        // Freeze retries against the rolled-back cart; gift discounts can change other discounts.
        failedSignature = signature(cart);
      } catch (cleanupError) {
        unsafe = true;
        failure = 'Your gift offer needs to be refreshed before checkout. Please retry.';
      }
    }
    if (dirty) {
      try { await refreshCart(cart); dirty = false; }
      catch (error) {
        unsafe = true;
        failure = 'Please refresh your cart before checkout to see the latest items and totals.';
      }
    }
    render(cart);
    lockCart(false);
    return cart;
  }

  window.TreezynCartRewards = {
    sync,
    canCheckout: (cart) => !unsafe && (!gifts(cart).length || (supported(cart) && spend(cart) >= config.giftThreshold && complete(cart))),
  };
})();
